import { Component, Inject, OnInit, signal } from '@angular/core';
import { EscapeDialogHelper } from '../../helpers/escape-dialog.helper';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { InsurerPickUp } from '../../model/insurer.model';
import { MessagingService } from '../../components/messaging/messaging.service';
import moment, { Moment } from 'moment';
import { PickUpFormRequest } from '../../model/pickUpFormRequest.model';
import { EIO_DATE_BE_FORMAT, EIO_DATE_UI_FORMAT_MOMENT } from '../../consts/localization.const';
import { ToolbarService } from '../../services/toolbar.service';
import { DateInputHelper } from '../../helpers/date-input.helper';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../directives/browser-auto-fill-disabled.directive';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-pickup-job',
  templateUrl: './pickup-job.component.html',
  styleUrls: ['./pickup-job.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatHint,
    MatProgressBar,
    MatDialogActions,
    MatButton
  ]
})
export class PickupJobComponent implements OnInit {
  pickupForm: UntypedFormGroup;
  insurers: InsurerPickUp[];
  availableDates: Moment[] = [];
  minDate: Moment;
  showInspectionDate = signal<boolean>(false);
  pending = signal<boolean>(false);
  showProgressBar = signal<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<PickupJobComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private message: MessagingService,
    public toolbarService: ToolbarService,
    private escapeDialogRef: EscapeDialogHelper,
    public dateInputHelper: DateInputHelper
  ) {}

  get insurer() {
    return this.pickupForm.get('insurer');
  }

  get rego() {
    return this.pickupForm.get('rego');
  }

  get claimNumber() {
    return this.pickupForm.get('claimNumber');
  }

  get inspectionDate() {
    return this.pickupForm.get('inspectionDate');
  }

  ngOnInit(): void {
    this.escapeDialogRef.escapeDialog(this.dialogRef);

    this.pickupForm = this.fb.group({
      insurer: ['', Validators.required],
      rego: ['', Validators.required],
      claimNumber: [''],
      inspectionDate: ''
    });

    this.insurers = this.data.insurers;
    this.minDate = this.data.currentTimeZone ? moment().tz(this.data.currentTimeZone) : moment();
  }

  selectInsurer() {
    const insurer = this.insurers.find((ins) => ins.insurerId === this.insurer.value);
    if (insurer.rule && insurer.rule.actionRules) {
      const validators = [];
      insurer.rule.actionRules.forEach((rule) => {
        if (rule.comparedAttribute === 'inspectionDate') {
          if (rule.attributeComparator === 'valuesNotIn' && rule.compareValues) {
            let found = false;
            const values = rule.compareValues.split(';');
            for (const value of values) {
              found = insurer.repairerLabels.includes(value);
              if (found) {
                return;
              }
            }
            this.showInspectionDate.set(!found);
          }
        }
      });
    }
    if (this.showInspectionDate()) {
      this.userService.getNextAvailableInspectionDates(this.insurer.value).subscribe(
        (availableDates) => availableDates.forEach((date) => this.availableDates.push(moment.unix(date))),
        () => this.message.error('Could not retrieve the next available inspection dates.')
      );
    }
    if (!insurer.rule.actionRules.find((rule) => rule.comparedAttribute === 'claimNumber')) {
      this.claimNumber.clearValidators();
    }
    this.claimNumber.updateValueAndValidity();
  }

  availableDatesFilter = (m: Moment | null): boolean => {
    let found = false;
    for (const date of this.availableDates) {
      found =
        (m || moment()).date() === date.date() &&
        (m || moment()).month() === date.month() &&
        (m || moment()).year() === date.year();
      if (found) {
        break;
      }
    }
    return found;
  };

  pickUpJob() {
    this.pending.set(true);
    this.showProgressBar.set(true);
    this.toolbarService.setCloudState('SAVING');
    const request: PickUpFormRequest = {
      rego: this.rego.value
    };
    if (this.claimNumber.value) {
      request.claimNumber = this.claimNumber.value;
    }
    if (this.inspectionDate.value) {
      request.inspectionDate = this.availableDates
        .find((d) => d.format(EIO_DATE_BE_FORMAT) === moment(this.inspectionDate.value).format(EIO_DATE_BE_FORMAT))
        .unix();
    }
    this.userService.createPendingAssessment(this.insurer.value, request).subscribe(
      (response) => {
        if (response) {
          this.dialogRef.close({
            status: response.statusCode,
            result: response.result,
            inspectionDate:
              response.result === 'PreAssigned'
                ? response.inspectionDate
                  ? moment(response.inspectionDate.gmtDate).format(EIO_DATE_UI_FORMAT_MOMENT)
                  : ''
                : this.showInspectionDate()
                  ? this.inspectionDate.value
                    ? moment(this.inspectionDate.value).format(EIO_DATE_UI_FORMAT_MOMENT)
                    : 'Invalid date'
                  : ''
          });
        } else {
          // We just receive null when there is an error.
          this.message.error('There was a technical problem while trying to process your request');
        }
      },
      () => {
        this.message.error('There was a technical problem while trying to process your request');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
        this.showProgressBar.set(false);
      },
      () => {
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
        this.showProgressBar.set(false);
      }
    );
  }
}
